import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Leaf, ShieldCheck, Sprout, Flame, Languages, Star, Ear } from "lucide-react";
import { Reveal, Label } from "@/components/Reveal";
import TopoBackground from "@/components/TopoBackground";
import WisdomProgression from "@/components/WisdomProgression";

const IMG = {
  hero: "https://images.unsplash.com/photo-1760199025516-51f1b0676ae8?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400",
  philosophy: "https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  interactive: "https://images.unsplash.com/photo-1712846040326-a3d06c0b1667?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  companion: "https://images.unsplash.com/photo-1594675610313-f427344ac988?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  future: "https://images.unsplash.com/photo-1495571758719-6ec1e876d6ae?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000",
  closing: "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400",
};

const FIRST_ARCHIVE = [
  "Seasonal change", "Water", "Plants", "Fire",
  "Song", "Language", "Story", "Relationships",
];

const PLACE_EXAMPLES = [
  { icon: Leaf, place: "Standing beside a tree", info: "This species flowers after the first summer rains." },
  { icon: MapPin, place: "Walking a cultural trail", info: "This path follows a songline remembered across generations." },
  { icon: Sprout, place: "Monitoring restoration", info: "Traditional burning renewed this Country three seasons ago." },
  { icon: Sprout, place: "Collecting seed", info: "Seed gathered here has strengthened restoration across three communities." },
  { icon: Languages, place: "Learning language", info: "The name for this place carries its story and its law." },
  { icon: Flame, place: "Fire planning", info: "Cool-burn knowledge held here guides this season's care." },
  { icon: Star, place: "Looking at the stars", info: "These constellations mark seasons, travel and ceremony." },
  { icon: Ear, place: "Listening to Elders", info: "This knowledge belongs to community, and is shared on community terms." },
];

const QUESTIONS = [
  "What grows here?",
  "What story belongs to this place?",
  "Which language names exist for this species?",
  "What seed should be collected this season?",
  "What happened here before the road?",
  "Who should I speak with about this Country?",
  "What restoration activities have occurred?",
];

const CHAIN = [
  "Country", "Language", "Story", "People", "Plants", "Animals",
  "Fire", "Water", "Season", "Cultural Practice", "Management", "Future Generations",
];

const STEWARDSHIP_SUPPORTS = [
  "Healthy Country planning", "Fire management", "Seed collection", "Species monitoring",
  "Cultural heritage", "Language revitalisation", "Youth learning", "Community governance",
  "Restoration", "Decision-making",
];

const GOVERNANCE = [
  "Who can access knowledge.",
  "What remains protected.",
  "What can be shared.",
  "What stays on Country.",
];

export default function AIOnCountry() {
  return (
    <div data-testid="ai-on-country-page">
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden">
        <img src={IMG.hero} alt="Traditional Owners walking through Country at dawn" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050806]/60 via-[#050806]/35 to-[#050806]" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <Label className="mb-6 block">AI on Country</Label>
            <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#F4F1ED]">
              AI That Learns From Place
            </h1>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-[#D8D4CE] leading-relaxed font-light">
              Artificial Intelligence does not create wisdom. Wisdom already exists within
              communities, stories and Country. AI simply helps reveal relationships that
              strengthen stewardship. Technology becomes another way of listening — never
              another way of replacing.
            </p>
            <p className="mt-6 wk-label">Where knowledge meets place. Where memory becomes stewardship.</p>
          </motion.div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[#A8A5A1] text-xs tracking-[0.3em] uppercase">
          Walk with us
        </div>
      </section>

      {/* PHILOSOPHY — Country is the first knowledge system */}
      <section className="relative py-28 md:py-40 border-b border-white/5 overflow-hidden">
        <img src={IMG.philosophy} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-[#050806]/75" />
        <TopoBackground opacity={0.07} />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-6">Philosophy</Label>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-[#F4F1ED] max-w-3xl">
              Country Is The First Knowledge System
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-2xl text-lg text-[#A8A5A1] leading-relaxed">
              Long before libraries, books or databases, knowledge was held within Country
              itself. Country has always been the first knowledge system.
            </p>
          </Reveal>
          <div className="mt-12 flex flex-wrap gap-3">
            {FIRST_ARCHIVE.map((w, i) => (
              <Reveal key={w} delay={i * 0.05}>
                <span className="inline-block px-5 py-2.5 rounded-full border border-white/10 text-[#D8D4CE] text-sm hover:border-[#D4A373]/60 hover:text-[#D4A373] transition-colors duration-500">
                  {w}
                </span>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-14 max-w-2xl font-display text-2xl md:text-3xl text-[#F4F1ED] leading-snug">
              The role of technology is not to replace Country.{" "}
              <span className="text-[#D4A373]">Artificial Intelligence becomes another way of listening — not another way of controlling.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* INTERACTIVE EXPERIENCE */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-white/8">
              <img src={IMG.interactive} alt="A person holding a device while standing on Country" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050806]/70 to-transparent" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <Label className="block mb-4">The experience</Label>
              <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED]">
                As you move through the landscape, knowledge quietly appears.
              </h2>
              <p className="mt-6 text-[#A8A5A1] leading-relaxed max-w-xl">
                Not intrusive. Not augmented-reality overload. Simple. Elegant. The
                knowledge was always here — the technology simply helps reveal relationships.
              </p>
            </Reveal>
            <div className="mt-10 space-y-3">
              {PLACE_EXAMPLES.map((ex, i) => (
                <Reveal key={ex.place} delay={i * 0.08}>
                  <div className="group flex items-start gap-4 p-5 rounded-xl border border-white/8 hover:border-[#D4A373]/40 hover:bg-[#101612] transition-all duration-500">
                    <ex.icon size={20} className="mt-1 text-[#D4A373] shrink-0" />
                    <div>
                      <p className="text-sm text-[#A8A5A1]">{ex.place}</p>
                      <p className="mt-1 font-display text-xl text-[#F4F1ED]">“{ex.info}”</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* AI COMPANION */}
      <section className="relative py-24 md:py-32 border-b border-white/5 overflow-hidden">
        <img src={IMG.companion} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-[#050806]/80" />
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-4">The companion</Label>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-[#F4F1ED]">
              A Steward, Not A Search Engine
            </h2>
            <p className="mt-6 max-w-2xl text-[#A8A5A1] leading-relaxed">
              Rather than searching files, people simply ask questions naturally. The AI
              responds using community-approved knowledge — honouring cultural authority
              and encouraging relationship rather than replacing it.
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 gap-4">
            {QUESTIONS.map((q, i) => (
              <Reveal key={q} delay={i * 0.06}>
                <div className="flex items-center gap-4 p-5 rounded-xl bg-[#0C110E]/80 border border-white/8">
                  <span className="font-display text-lg text-[#D4A373]/50">?</span>
                  <p className="font-display text-xl md:text-2xl text-[#F4F1ED]">{q}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-10 text-[#A8A5A1]">
              Every response respects governance. Every response honours cultural authority.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CONNECTED KNOWLEDGE — animated chain */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-4">Connected knowledge</Label>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED] max-w-3xl">
              Nothing exists in isolation. Knowledge becomes a living web.
            </h2>
          </Reveal>

          <div className="mt-16 relative">
            <span className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#D4A373]/40 to-transparent" />
            <div className="space-y-4">
              {CHAIN.map((node, i) => (
                <Reveal key={node} delay={i * 0.05}>
                  <div className={`relative flex ${i % 2 === 0 ? "justify-start md:pr-[52%]" : "justify-end md:pl-[52%]"}`}>
                    <div className="relative inline-flex items-center gap-3 px-6 py-3 rounded-full border border-white/10 bg-[#0C110E] hover:border-[#D4A373]/50 transition-colors duration-500">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#D4A373]" />
                      <span className="font-display text-xl md:text-2xl text-[#F4F1ED]">{node}</span>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* STEWARDSHIP INTELLIGENCE */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <Reveal>
              <div>
                <Label className="block mb-4">Stewardship Intelligence</Label>
                <h2 className="font-display font-light text-4xl sm:text-5xl text-[#F4F1ED]">
                  From Information To Stewardship
                </h2>
                <p className="mt-6 text-[#A8A5A1] leading-relaxed">
                  Artificial Intelligence helps us process information. Stewardship
                  Intelligence helps us care for what matters — the capacity to recognise
                  relationships between people, place, culture and future generations.
                  WisdomKeepers exists to cultivate Stewardship Intelligence across
                  communities, organisations and society.
                </p>
                <div className="mt-8 space-y-6">
                  <div className="p-6 rounded-xl border border-white/8">
                    <p className="wk-label mb-2">Traditional archives ask</p>
                    <p className="font-display text-2xl text-[#A8A5A1]">“What do we know?”</p>
                  </div>
                  <div className="p-6 rounded-xl border border-[#D4A373]/40 bg-[#101612]">
                    <p className="wk-label mb-2">WisdomKeepers asks</p>
                    <p className="font-display text-2xl text-[#F4F1ED]">“How should we care?”</p>
                  </div>
                </div>
                <p className="mt-8 font-display text-2xl text-[#D4A373]">
                  The outcome is not more information. The outcome is wiser action.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="grid grid-cols-2 gap-px bg-white/5 border border-white/5 rounded-lg overflow-hidden">
                {STEWARDSHIP_SUPPORTS.map((s, i) => (
                  <div key={s} className="bg-[#0A0F0C] p-6 hover:bg-[#101612] transition-colors duration-500">
                    <span className="font-display text-lg text-[#D4A373]/40">{String(i + 1).padStart(2, "0")}</span>
                    <p className="mt-2 text-[#F4F1ED]">{s}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COMMUNITY GOVERNANCE */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <Label className="block mb-6">Governance</Label>
            <h2 className="font-display font-light text-4xl sm:text-5xl text-[#F4F1ED]">
              Communities Remain The Authority
            </h2>
            <p className="mt-6 text-lg text-[#A8A5A1]">
              AI never decides. Communities decide. Traditional Owners determine:
            </p>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 gap-4 text-left">
            {GOVERNANCE.map((g, i) => (
              <Reveal key={g} delay={i * 0.08}>
                <div className="p-6 rounded-xl border border-white/8 flex items-center gap-4">
                  <ShieldCheck size={20} className="text-[#D4A373] shrink-0" />
                  <p className="font-display text-xl text-[#F4F1ED]">{g}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-14 flex flex-wrap justify-center gap-x-8 gap-y-3 font-display text-2xl md:text-3xl">
              <span className="text-[#A8A5A1]">AI never decides.</span>
              <span className="text-[#A8A5A1]">Technology never owns.</span>
              <span className="text-[#F4F1ED]">Communities govern.</span>
              <span className="text-[#F4F1ED]">Country teaches.</span>
              <span className="text-[#D4A373]">Future generations inherit.</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FUTURE VISION */}
      <section className="relative py-32 md:py-44 overflow-hidden border-b border-white/5">
        <img src={IMG.future} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050806] via-[#050806]/70 to-[#050806]" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-6">The future</Label>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-[#F4F1ED]">
              Imagine walking through a living knowledge landscape.
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-8 space-y-5 text-lg text-[#D8D4CE] leading-relaxed max-w-2xl">
              <p>
                Imagine standing beneath an ancient tree. Your phone quietly recognises
                where you are — not through surveillance, but through stewardship.
              </p>
              <p>
                It understands that this place holds stories, language, species, fire
                history, restoration work, family memory and community decisions.
              </p>
              <p>
                The knowledge does not come from the internet. It comes from the people who
                have cared for this Country across generations. Artificial Intelligence
                simply helps reveal those relationships.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 font-display text-3xl text-[#D4A373]">The wisdom was always here.</p>
          </Reveal>
        </div>
      </section>

      {/* Recurring philosophy thread */}
      <section className="py-20 md:py-28 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <WisdomProgression dark />
        </div>
      </section>

      {/* CLOSING */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <img src={IMG.closing} alt="Country at sunrise" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050806] via-[#050806]/55 to-[#050806]/25" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-32 text-center">
          <div className="rounded-3xl bg-[#F4F1ED]/10 backdrop-blur-md border border-white/15 px-8 py-12 md:px-14 md:py-16 shadow-[0_20px_60px_rgba(0,0,0,0.25)]">
            <Reveal>
              <p className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED] leading-[1.25]">
                Wisdom does not sit inside technology.{" "}
                <span className="text-[#D4A373]">Wisdom sits in place.</span>
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-2xl mx-auto text-lg text-[#EDEAE4] leading-relaxed">
                Technology should help us remember. Country teaches us how to care. Future
                generations will judge us not by the intelligence we created, but by the
                wisdom we chose to steward.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.25}>
            <Link
              to="/partner"
              data-testid="ai-country-cta"
              className="mt-11 inline-flex items-center gap-2 px-8 py-4 rounded-md bg-[#D4A373] text-[#050806] font-medium hover:bg-[#E6CCB2] transition-colors duration-500"
            >
              Bring this to your Country <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
